import { useCallback } from "react";

import { useToast } from "../../../../components";
import {
  CreateOrUpdateTaskDefinitionMutation,
  TaskDefinitionFragment,
  TaskDefinitionType,
  useCreateOrUpdateTaskDefinitionMutation,
  useDeleteTaskDefinitionMutation,
  useGreenhouseSetupQuery,
  useTaskDefinitionsQuery,
} from "../../../graphql";
import { CandidateRediscoveryConfiguration } from "./types";
import {
  parseCandidateRediscoveryConfiguration,
  stringifyCandidateRediscoveryConfiguration,
} from "./utils";

// customInstructions are not currently used, but remain here for future use
const NEW_CONFIGURATION: CandidateRediscoveryConfiguration = {
  customInstructions: "",
};

export type CreateRediscoveryTaskDefinitionProps = {
  callback?: (data: CreateOrUpdateTaskDefinitionMutation) => void;
};

export type TaskDefinitionResult = {
  taskDefinition: TaskDefinitionFragment | null;
  loading: boolean;
  error: Error | undefined;
  parsedConfiguration: CandidateRediscoveryConfiguration | null;
};

type RediscoveryTaskReturn = {
  taskDefinition: TaskDefinitionResult;
  // taskRun: TaskRunResult;
  createRediscoveryTaskDefinition: {
    mutation: (props?: CreateRediscoveryTaskDefinitionProps) => void;
    loading: boolean;
    error: Error | undefined;
  };
  deleteRediscoveryTaskDefinition: {
    mutation: () => void;
    loading: boolean;
    error: Error | undefined;
  };
};

const REDISCOVERY_TASK_TYPE = TaskDefinitionType.Rediscovery;

const useRediscoveryTask: () => RediscoveryTaskReturn = () => {
  const toast = useToast();

  // Check Greenhouse setup; Rediscovery requires Greenhouse
  const greenhouseSetupQuery = useGreenhouseSetupQuery();

  // Get task definitions
  const taskDefinitionsQuery = useTaskDefinitionsQuery({
    variables: {
      type: REDISCOVERY_TASK_TYPE,
    },
  });
  const taskDefinitions = taskDefinitionsQuery.data?.taskDefinitions;
  const taskDefinition =
    taskDefinitions && taskDefinitions.length > 0 ? taskDefinitions[0] : null;

  // Function to create a new task definition
  const [createTaskDefinition, createOrUpdateTaskDefinitionMutation] =
    useCreateOrUpdateTaskDefinitionMutation();
  const createRediscoveryTaskDefinition = useCallback(
    (props?: CreateRediscoveryTaskDefinitionProps) => {
      const { callback } = props || {};
      const isGreenhouseConfigured =
        greenhouseSetupQuery.data?.greenhouseSetup?.valid === true;
      if (!isGreenhouseConfigured) {
        toast({
          title: "Greenhouse required",
          description:
            "Candidate Rediscovery requires an active Greenhouse integration.",
          status: "error",
        });
        return;
      }
      createTaskDefinition({
        variables: {
          type: REDISCOVERY_TASK_TYPE,
          configurationJson:
            stringifyCandidateRediscoveryConfiguration(NEW_CONFIGURATION),
        },
        onCompleted: (data) => {
          const { createOrUpdateTaskDefinition: newTaskDefinition } = data;
          if (newTaskDefinition) {
            callback?.(data);
            taskDefinitionsQuery.refetch();
            toast({
              title: "Success",
              description: "Candidate Rediscovery is now enabled.",
              status: "success",
            });
          }
        },
      });
    },
    [
      createTaskDefinition,
      greenhouseSetupQuery.data,
      taskDefinitionsQuery,
      toast,
    ]
  );

  // Function to delete the task definition
  const [deleteTaskDefinition, deleteTaskDefinitionMutation] =
    useDeleteTaskDefinitionMutation();
  const deleteRediscoveryTaskDefinition = useCallback(() => {
    deleteTaskDefinition({
      variables: {
        id: taskDefinition?.id || "",
        type: REDISCOVERY_TASK_TYPE,
      },
      onCompleted: () => {
        taskDefinitionsQuery.refetch();
        toast({
          title: "Success",
          description: "Candidate Rediscovery is now disabled.",
          status: "success",
        });
      },
    });
  }, [deleteTaskDefinition, taskDefinition, taskDefinitionsQuery, toast]);

  return {
    taskDefinition: {
      loading: taskDefinitionsQuery.loading,
      error: taskDefinitionsQuery.error,
      taskDefinition: taskDefinition || null,
      parsedConfiguration:
        parseCandidateRediscoveryConfiguration(taskDefinition),
    },
    createRediscoveryTaskDefinition: {
      mutation: createRediscoveryTaskDefinition,
      loading: createOrUpdateTaskDefinitionMutation.loading,
      error: createOrUpdateTaskDefinitionMutation.error,
    },
    deleteRediscoveryTaskDefinition: {
      mutation: deleteRediscoveryTaskDefinition,
      loading: deleteTaskDefinitionMutation.loading,
      error: deleteTaskDefinitionMutation.error,
    },
  };
};

export default useRediscoveryTask;
